import { useEffect, useState } from 'react';

interface Options {
  /** Área rolável da conversa: a interseção é medida em relação a ela, não à janela. */
  root: Element | null;
  /** Só observa enquanto carregar mais antigas fizer sentido (há mais, nada em andamento nem falho). */
  enabled: boolean;
  onReach: () => void;
  /**
   * Muda a cada página acrescentada. Recria o observador: o navegador só avisa quando a interseção
   * MUDA, então, se a página recém-chegada ainda não afastar o topo da tela, só um observador novo
   * (que recebe o estado atual ao começar) pede a seguinte.
   */
  loadedPages: number;
}

/**
 * Chama `onReach` quando o sentinela (devolvido como ref de callback) chega perto do TOPO da área
 * rolável. Sem `IntersectionObserver` não faz nada: o botão "Carregar mensagens anteriores"
 * continua disponível. Enquanto `enabled` é falso (busca em andamento, erro) nada é observado,
 * então uma falha nunca vira laço de requisições.
 */
export function useTopSentinel({
  root,
  enabled,
  onReach,
  loadedPages,
}: Options): (element: Element | null) => void {
  const [sentinel, setSentinel] = useState<Element | null>(null);

  useEffect(() => {
    if (!enabled || !root || !sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReach();
      },
      // 200px de antecedência: a página anterior chega antes de a pessoa encostar no topo.
      { root, rootMargin: '200px 0px 0px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, root, sentinel, onReach, loadedPages]);

  return setSentinel;
}
